import { useState, useEffect } from "react";
import { Link, useRoute } from "wouter";
import { AdventureLogo } from "@/components/adventure-logo";
import { NavPanel } from "@/components/nav-panel";
import { useSession } from "@/lib/member-auth";
import { MEMBERS_PORTAL_LIVE } from "@shared/members-launch";
import { effectiveScrollY } from "@/lib/body-scroll-lock";

/* ─── NAVBAR ─────────────────────────────────────────────────────────────── */

export function Navbar({ dark = false }: { dark?: boolean }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Workshop detail pages get a single-CTA nav: hide "Get a Proposal" (it still
  // lives inside the burger menu) and move the burger to the right so ticket
  // purchase is the only competing action. `:slug` matches `/workshops/:slug`
  // and `/events/:slug` but NOT the two-segment `/workshops/:slug/success`
  // nor the bare `/workshops` listing.
  const [isWorkshopSlug] = useRoute("/workshops/:slug");
  const [isEventSlug] = useRoute("/events/:slug");
  const isWorkshopDetail = isWorkshopSlug || isEventSlug;
  // The member entry is visible in the bar itself (Patrick, Oct 1: it was
  // hard to find sign-in from the site). Logged-out until the session settles.
  const { data: sessionData, isPending: sessionPending } = useSession();
  const isLoggedIn = !sessionPending && !!sessionData?.user;

  // While the session is resolving, render the logged-out variant — no
  // flash of the wrong state, and it self-corrects the instant it settles.

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        // effectiveScrollY, not window.scrollY: a popup's scroll lock reads as
        // scrollY 0 and would drop the bar's background behind the popup.
        setScrolled(effectiveScrollY() > 100);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scrolled state forces light bg.
  // 2026-09-09: `backdrop-blur-sm` was on a FIXED full-width bar sitting at 98%
  // opaque cream — the blur was invisible behind it, but the compositor still
  // re-blurred the strip under the bar on every scroll frame. Dropped, along
  // with the backdrop-filter transition token on the <nav> below.
  const bgClass = scrolled
    ? "bg-cream/98 border-b border-charcoal/8"
    : "bg-transparent";

  const isDarkText = scrolled || !dark;
  const linkColor = isDarkText ? "text-charcoal" : "text-cream";
  const linkHover = isDarkText ? "hover:text-indigo" : "hover:text-indigo-lt";
  const iconColor = isDarkText ? "bg-charcoal" : "bg-cream";
  const logoVariant = (scrolled || !dark) ? "default" : "light";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${bgClass}`}
        style={{ WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden", transform: "translate3d(0,0,0)" }}
      >
        <div className="container-wide flex items-center justify-between h-[72px]">
          <Link href="/">
            <AdventureLogo variant={logoVariant} />
          </Link>

          <div className="flex items-center gap-8">
            {MEMBERS_PORTAL_LIVE && (
              <Link
                href={isLoggedIn ? "/dashboard" : "/login"}
                className={`type-label hidden md:inline ${linkColor} ${linkHover} transition-colors`}
                data-testid={isLoggedIn ? "navbar-dashboard" : "navbar-signin"}
              >
                {isLoggedIn ? "Dashboard" : "Sign in"}
              </Link>
            )}
            {/* Menu trigger */}
            {/* px/py + matching negative margins give the 18x14 icon a ≥44x44
                tap target without moving the visual icon or the nav layout. */}
            <button
              onClick={() => setPanelOpen(true)}
              className={`flex items-center gap-2.5 ${linkColor} cursor-pointer group px-[14px] py-[16px] -mx-[14px] -my-[16px]`}
              aria-haspopup="dialog"
              aria-label="Open navigation menu"
            >
              <div className="relative w-[18px] h-[14px] flex flex-col justify-between">
                <span className={`block h-[2px] w-full ${iconColor} transition-all group-hover:w-3`} />
                <span className={`block h-[2px] w-full ${iconColor}`} />
                <span className={`block h-[2px] w-full ${iconColor} transition-all group-hover:w-3.5`} />
              </div>
              <span className={`type-label ${linkColor} ${linkHover} transition-colors hidden sm:inline`}>
                Menu
              </span>
            </button>

            {/* CTA — hidden on workshop detail pages (it still lives in the
                burger menu) so ticket purchase is the only competing action.
                Removing it leaves the burger as the rightmost nav element.
                The aria-label repeats the visible text on purpose. It used to
                read "Contact us", which tripped label-content-name-mismatch
                (voice-control users say the words they can see), but it cannot
                simply be dropped: the label text lives inside a `hidden
                md:block` button, so below md the anchor would have no
                accessible name at all. Matching the visible text satisfies
                both rules. */}
            {!isWorkshopDetail && (
              <Link href="/contact" aria-label="Get a Proposal">
                <button className="hidden md:block h-[44px] px-6 bg-indigo text-cream type-label rounded-none hover:bg-indigo/90 transition-colors cursor-pointer">
                  Get a Proposal
                </button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <NavPanel isOpen={panelOpen} onClose={() => setPanelOpen(false)} />
    </>
  );
}
